import * as React from "react";
import { Building2, Users } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useAuth } from "../../lib/auth/hooks";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useI18n } from "../../lib/i18n";
import { stageAdditionalOrgIds } from "../../lib/organization-context/OrganizationContextProvider";
import {
  createOrganization,
  fetchOrganizations,
  addOrganizationMember,
  fetchOrganizationRoles,
  fetchMockedOrgCount,
  clearMockedOrgs,
  fetchMockedUserCount,
  clearMockedUsers,
} from "../../lib/api/users";
import type { Organization, OrganizationScopedRole } from "../../lib/api/users";
import {
  createProduct,
  fetchProductModels,
  fetchMockedProductCount,
  clearMockedProducts,
} from "../../lib/api/products";
import type { ProductModel } from "../../lib/api/products";
import { authenticatedProApiFetch } from "../../lib/api/pro-api.client";
import { OrganizationPickerSelect } from "../users/OrganizationPickerSelect";
import * as layout from "../organizations/OrganizationEditPage.css";

// ─── Fake data generators ─────────────────────────────────────────────────────

const FIRST_NAMES = [
  "Alex", "Jordan", "Morgan", "Taylor", "Casey", "Riley", "Avery", "Quinn",
  "Blake", "Cameron", "Drew", "Emery", "Finley", "Harper", "Jamie", "Kendall",
  "Logan", "Mason", "Noah", "Parker", "Reese", "Sage", "Skyler", "Tyler",
];
const LAST_NAMES = [
  "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis",
  "Wilson", "Anderson", "Taylor", "Thomas", "Jackson", "White", "Harris", "Martin",
  "Thompson", "Young", "Allen", "King", "Wright", "Scott", "Green", "Baker",
];
const ORG_PREFIXES = [
  "Sunrise", "Mountain", "Valley", "Pacific", "Coastal", "Summit", "Horizon",
  "Pioneer", "Pinnacle", "Liberty", "Eagle", "Maple", "Cedar", "Willow", "Aspen",
];
const ORG_SUFFIXES = [
  "Energy", "Solar", "Power", "Systems", "Solutions", "Group", "Holdings",
  "Properties", "Industries", "Enterprises", "Partners", "Associates",
];
const CITIES = [
  ["Salt Lake City", "UT"], ["Denver", "CO"], ["Phoenix", "AZ"], ["Las Vegas", "NV"],
  ["Boise", "ID"], ["Tucson", "AZ"], ["Albuquerque", "NM"], ["Colorado Springs", "CO"],
  ["Reno", "NV"], ["Scottsdale", "AZ"], ["Henderson", "NV"], ["Mesa", "AZ"],
];
const PRODUCT_NAME_PREFIXES = [
  "Smith", "Johnson", "Rivera", "Chen", "Patel", "Kim", "Martinez", "Thompson",
  "Anderson", "Wilson", "Davis", "Garcia", "Miller", "Moore", "Taylor",
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function makeName(): { first: string; last: string } {
  return { first: pick(FIRST_NAMES), last: pick(LAST_NAMES) };
}

function makeEmail(first: string, last: string, index: number): string {
  const domains = ["gmail.com", "yahoo.com", "outlook.com", "icloud.com", "proton.me"];
  return `${first.toLowerCase()}.${last.toLowerCase()}${index}@${pick(domains)}`;
}

function makeOrgName(): string {
  // Append a short random tag to avoid name collisions across multiple generation runs.
  // e.g. "Mountain Energy · K4X"
  const tag = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `${pick(ORG_PREFIXES)} ${pick(ORG_SUFFIXES)} · ${tag}`;
}

function makeOrgSlug(name: string, index: number): string {
  return `${name.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}-${index}`;
}

function makeProductName(modelName: string): string {
  return `${pick(PRODUCT_NAME_PREFIXES)} Residence ${modelName}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface GeneratedRecord {
  id: string;
  label: string;
  detail?: string;
}

interface SectionState {
  count: string;
  running: boolean;
  results: GeneratedRecord[];
  error: string | null;
  done: boolean;
}

const defaultSection = (): SectionState => ({
  count: "5",
  running: false,
  results: [],
  error: null,
  done: false,
});

// ─── Component ────────────────────────────────────────────────────────────────

export function MockDataPage() {
  const { accessToken, refreshUser } = useAuth();
  const { t } = useI18n();
  const { confirm, ConfirmModal } = useConfirmModal();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("mock-data");
  const token = accessToken ?? localStorage.getItem("access_token");

  const [orgs, setOrgs] = React.useState<SectionState>(defaultSection());
  const [users, setUsers] = React.useState<SectionState>(defaultSection());
  const [products, setProducts] = React.useState<SectionState>(defaultSection());
  const [productModels, setProductModels] = React.useState<ProductModel[]>([]);

  // Products target org
  const [targetOrgId, setTargetOrgId] = React.useState<string>("");

  // Users target org + role
  const [userOrgId, setUserOrgId] = React.useState<string>("");
  const [userRoleId, setUserRoleId] = React.useState<string>("");
  const [userOrgRoles, setUserOrgRoles] = React.useState<OrganizationScopedRole[]>([]);
  const [userOrgRolesLoading, setUserOrgRolesLoading] = React.useState(false);

  const [createdOrgs, setCreatedOrgs] = React.useState<GeneratedRecord[]>([]);
  const [existingOrgs, setExistingOrgs] = React.useState<Organization[]>([]);
  const [orgsLoading, setOrgsLoading] = React.useState(false);

  // Mocked object counts
  const [mockedOrgCount, setMockedOrgCount] = React.useState<number | null>(null);
  const [mockedUserCount, setMockedUserCount] = React.useState<number | null>(null);
  const [mockedProductCount, setMockedProductCount] = React.useState<number | null>(null);
  const [clearing, setClearing] = React.useState<"orgs" | "users" | "products" | null>(null);

  // Load org-scoped roles when target org changes.
  // Clear roleId eagerly before the fetch so stale values are never sent.
  React.useEffect(() => {
    setUserRoleId("");
    setUserOrgRoles([]);
    if (!token || !userOrgId) return;
    setUserOrgRolesLoading(true);
    fetchOrganizationRoles(token, userOrgId)
      .then((roles) => {
        setUserOrgRoles(roles);
        setUserRoleId(roles.length === 1 ? (roles[0]?.id ?? "") : "");
      })
      .catch(() => setUserOrgRoles([]))
      .finally(() => setUserOrgRolesLoading(false));
  }, [token, userOrgId]);

  const refreshCounts = React.useCallback(() => {
    if (!token) return;
    fetchMockedOrgCount(token).then(setMockedOrgCount).catch(() => {});
    fetchMockedUserCount(token).then(setMockedUserCount).catch(() => {});
    fetchMockedProductCount(token).then(setMockedProductCount).catch(() => {});
  }, [token]);

  // Load product models, existing organizations, and mocked counts
  React.useEffect(() => {
    if (!token) return;
    fetchProductModels(token).then(setProductModels).catch(() => {});
    setOrgsLoading(true);
    fetchOrganizations(token)
      .then(setExistingOrgs)
      .catch(() => {})
      .finally(() => setOrgsLoading(false));
    refreshCounts();
  }, [token, refreshCounts]);

  const patchOrgs = (patch: Partial<SectionState>) => setOrgs((p) => ({ ...p, ...patch }));
  const patchUsers = (patch: Partial<SectionState>) => setUsers((p) => ({ ...p, ...patch }));
  const patchProducts = (patch: Partial<SectionState>) => setProducts((p) => ({ ...p, ...patch }));

  // ── Generate Organizations ──────────────────────────────────────────────────
  const generateOrgs = async () => {
    if (!token) return;
    const n = Math.max(1, Math.min(50, parseInt(orgs.count) || 5));
    patchOrgs({ running: true, error: null, results: [], done: false });
    const results: GeneratedRecord[] = [];
    try {
      for (let i = 0; i < n; i++) {
        const name = makeOrgName();
        const [city, state] = pick(CITIES)!;
        // Use a seeded picsum URL so each org gets a consistent, unique placeholder logo
        const logoSeed = encodeURIComponent(`${name}-${i}`);
        const logoUrl = `https://picsum.photos/seed/${logoSeed}/200/200`;
        const org = await createOrganization(token, {
          name,
          slug: makeOrgSlug(name, i),
          logoUrl,
          city,
          state,
          country: "US",
          isMocked: true,
        }) as any;
        results.push({ id: org.id ?? crypto.randomUUID(), label: name, detail: `${city}, ${state}` });
        patchOrgs({ results: [...results] });
      }
      setCreatedOrgs((prev) => [...prev, ...results]);

      // Stage the new org IDs in sessionStorage so the org context includes them
      // when refreshUser() triggers a re-render with the updated org membership list.
      const newOrgIds = results.map((r) => r.id).filter((id) => id !== "?");
      stageAdditionalOrgIds(newOrgIds);

      // Refresh user so the NavigationBar org context updates immediately
      await refreshUser?.();

      // Also refresh the org dropdown for the products section and mocked counts
      fetchOrganizations(token).then(setExistingOrgs).catch(() => {});
      refreshCounts();
      patchOrgs({ done: true });
    } catch (err: any) {
      patchOrgs({ error: err?.message || t("pro.system.mockData.error.generateOrgs") });
    } finally {
      patchOrgs({ running: false });
    }
  };

  // ── Generate Users ──────────────────────────────────────────────────────────
  const generateUsers = async () => {
    if (!token) return;
    const n = Math.max(1, Math.min(50, parseInt(users.count) || 5));
    patchUsers({ running: true, error: null, results: [], done: false });
    const results: GeneratedRecord[] = [];
    // Use user-service register directly
    const userServiceUrl = import.meta.env.VITE_USER_SERVICE_URL_BROWSER || "http://localhost:3002/api";
    try {
      for (let i = 0; i < n; i++) {
        const { first, last } = makeName();
        const email = makeEmail(first, last, Date.now() + i);
        const password = "Password123";
        const res = await fetch(`${userServiceUrl}/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ firstName: first, lastName: last, email, password, is_mocked: true }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error((d as any).error || t("pro.system.mockData.error.registerFailed", { status: res.status }));
        }
        const data = await res.json() as any;
        // Registration returns { access_token, refresh_token } — no user object.
        // Decode the JWT sub claim to get the new user's id.
        const userId = (() => {
          try {
            const payload = JSON.parse(atob((data.access_token as string).split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
            return (payload.sub as string) ?? crypto.randomUUID();
          } catch {
            return data.user?.id ?? data.id ?? crypto.randomUUID();
          }
        })();

        // Set a placeholder avatar via admin PATCH (uses email as seed for consistency)
        if (userId !== "?") {
          const avatarSeed = encodeURIComponent(email);
          const avatarUrl = `https://i.pravatar.cc/150?u=${avatarSeed}`;
          await authenticatedProApiFetch(`/users/${userId}`, token, {
            method: "PATCH",
            body: JSON.stringify({ avatarUrl }),
          }).catch(() => {}); // non-fatal

          // Add to target org with org-scoped role
          if (userOrgId && userId !== "?") {
            await addOrganizationMember(token, userOrgId, {
              userId,
              roleId: userRoleId || null,
            }).catch(() => {}); // non-fatal — user is created even if org assignment fails
          }
        }

        const orgName = existingOrgs.find((o) => o.id === userOrgId)?.name;
        const roleName = userOrgRoles.find((r) => r.id === userRoleId)?.name;
        const detail = [email, orgName, roleName].filter(Boolean).join(" · ");
        results.push({ id: userId, label: `${first} ${last}`, detail });
        patchUsers({ results: [...results] });
      }
      refreshCounts();
      patchUsers({ done: true });
    } catch (err: any) {
      patchUsers({ error: err?.message || t("pro.system.mockData.error.generateUsers") });
    } finally {
      patchUsers({ running: false });
    }
  };

  // ── Generate Products ───────────────────────────────────────────────────────
  const generateProducts = async () => {
    if (!token) return;
    if (!targetOrgId) {
      patchProducts({ error: t("pro.system.mockData.error.selectOrg") });
      return;
    }
    const n = Math.max(1, Math.min(50, parseInt(products.count) || 5));
    patchProducts({ running: true, error: null, results: [], done: false });
    const results: GeneratedRecord[] = [];
    try {
      for (let i = 0; i < n; i++) {
        const model = productModels.length ? pick(productModels) : null;
        const name = makeProductName(model?.name ?? "System");
        const [city, state] = pick(CITIES)!;
        const product = await createProduct(
          {
            organizationId: targetOrgId,
            name,
            productModelId: model?.id,
            city,
            state,
            country: "US",
            isSimulated: false,
            isMocked: true,
            // Leave iotThingName unset — partner-api requires a real Smart product UUID
          },
          token,
        );
        results.push({
          id: product.id,
          label: name,
          detail: `${model?.name ?? "No model"} · ${city}, ${state}`,
        });
        patchProducts({ results: [...results] });
      }
      refreshCounts();
      patchProducts({ done: true });
    } catch (err: any) {
      patchProducts({ error: err?.message || t("pro.system.mockData.error.generateProducts") });
    } finally {
      patchProducts({ running: false });
    }
  };

  const handleClear = async (type: "orgs" | "users" | "products") => {
    if (!token) return;
    const labels = {
      orgs: t("pro.system.mockData.type.organizations"),
      users: t("pro.system.mockData.type.users"),
      products: t("pro.system.mockData.type.products"),
    };
    const counts = { orgs: mockedOrgCount, users: mockedUserCount, products: mockedProductCount };
    const n = counts[type];
    if (!n) return;
    const ok = await confirm(
      t("pro.system.mockData.confirm.clearTitle", { count: n, type: labels[type] }),
      t("pro.system.mockData.confirm.clearBody", { count: n, type: labels[type] }),
      t("pro.system.mockData.confirm.clear"),
    );
    if (!ok) return;
    setClearing(type);
    try {
      if (type === "orgs") { await clearMockedOrgs(token); await refreshUser?.(); }
      else if (type === "users") await clearMockedUsers(token);
      else await clearMockedProducts(token);
      // Clear the results table for the cleared section
      if (type === "orgs") patchOrgs({ results: [], done: false });
      else if (type === "users") patchUsers({ results: [], done: false });
      else patchProducts({ results: [], done: false });
      refreshCounts();
    } catch (err: any) {
      // Error feedback handled by ConfirmModal — swallow here
    } finally {
      setClearing(null);
    }
  };

  const sectionLayout = bindSectionLayout([
          { id: "mock-orgs", label: t("pro.system.mockData.orgs.title"), icon: <Building2 size={14} /> },
          { id: "mock-users", label: t("pro.system.mockData.users.title"), icon: <Users size={14} /> },
        ]);

  return (
    <>
      <DetailPageLayout
        header={{
          media: <Building2 size={28} color="#9CA3AF" />,
          title: t("pro.system.mockData.title"),
          subtitle: t("pro.system.mockData.subtitle"),
        }}
        {...sectionLayout}
      >
        {/* Organizations */}
        <CollapsibleSection
          id="mock-orgs"
          title={t("pro.system.mockData.orgs.title")}
          icon={<Building2 size={20} />}
          expanded={isExpanded("mock-orgs")}
          onToggleExpanded={() => toggle("mock-orgs")}
          statusIndicators={
            mockedOrgCount != null && mockedOrgCount > 0
              ? [
                  <span
                    key="count"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "2px 9px",
                      borderRadius: 9999,
                      background: "#FFF7ED",
                      color: "#C2410C",
                      border: "1px solid #FED7AA",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("pro.system.mockData.mockedCount", { count: mockedOrgCount })}
                  </span>,
                ]
              : undefined
          }
          contextActions={
            mockedOrgCount != null && mockedOrgCount > 0
              ? [
                  {
                    id: "clear",
                    label: t("pro.system.mockData.clear"),
                    color: "danger",
                    onClick: () => handleClear("orgs"),
                  },
                ]
              : undefined
          }
        >
          <div className={layout.cardInner}>
            <Text level="body-small" style={{ color: "#6B7280", marginBottom: 14 }}>
              {t("pro.system.mockData.orgs.description")}
            </Text>
            <MockSectionControls
              state={orgs}
              onCountChange={(v) => patchOrgs({ count: v })}
              onGenerate={generateOrgs}
              generateLabel={t("pro.system.mockData.orgs.generate")}
            />
            <MockSectionResults
              state={orgs}
              resultLabel={(r) => r.label}
              resultDetail={(r) => r.detail}
            />
          </div>
        </CollapsibleSection>

        {/* Users */}
        <CollapsibleSection
          id="mock-users"
          title={t("pro.system.mockData.users.title")}
          icon={<Users size={20} />}
          expanded={isExpanded("mock-users")}
          onToggleExpanded={() => toggle("mock-users")}
          statusIndicators={
            mockedUserCount != null && mockedUserCount > 0
              ? [
                  <span
                    key="count"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "2px 9px",
                      borderRadius: 9999,
                      background: "#FFF7ED",
                      color: "#C2410C",
                      border: "1px solid #FED7AA",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("pro.system.mockData.mockedCount", { count: mockedUserCount })}
                  </span>,
                ]
              : undefined
          }
          contextActions={
            mockedUserCount != null && mockedUserCount > 0
              ? [
                  {
                    id: "clear",
                    label: t("pro.system.mockData.clear"),
                    color: "danger",
                    onClick: () => handleClear("users"),
                  },
                ]
              : undefined
          }
        >
          <div className={layout.cardInner}>
            <Text level="body-small" style={{ color: "#6B7280", marginBottom: 14 }}>
              {t("pro.system.mockData.users.passwordNote")}
            </Text>

            <OrganizationPickerSelect
              label={t("pro.system.mockData.users.targetOrg")}
              value={userOrgId}
              onChange={setUserOrgId}
              organizations={existingOrgs.map((o) => ({
                id: o.id,
                name: o.name,
                logoUrl: o.logoUrl,
              }))}
              disabled={orgsLoading}
              placeholder={
                orgsLoading
                  ? t("pro.system.mockData.users.loadingOrgs")
                  : t("pro.system.mockData.users.selectOrg")
              }
            />

            {userOrgId && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>
                  {t("pro.system.mockData.users.role")}
                </label>
                <select
                  value={userRoleId}
                  onChange={(e) => setUserRoleId(e.target.value)}
                  disabled={userOrgRolesLoading || users.running}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #D1D5DB",
                    fontSize: 14,
                    background: "var(--color-inputBg, #fff)",
                    color: "var(--color-fg, #111)",
                  }}
                >
                  <option value="">{t("pro.system.mockData.users.noRole")}</option>
                  {userOrgRoles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <MockSectionControls
                state={users}
                onCountChange={(v) => patchUsers({ count: v })}
                onGenerate={generateUsers}
                generateLabel={t("pro.system.mockData.users.generate")}
              />
            </div>
            <MockSectionResults
              state={users}
              resultLabel={(r) => r.label}
              resultDetail={(r) => r.detail}
            />
          </div>
        </CollapsibleSection>

        {/* Products — disabled/hidden as before */}
        {false && (
          <div style={{ display: "none" }}>
            Products section stays disabled
          </div>
        )}
      </DetailPageLayout>
      <ConfirmModal />
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface MockSectionControlsProps {
  state: SectionState;
  onCountChange: (v: string) => void;
  onGenerate: () => void;
  generateLabel: string;
  disabled?: boolean;
}

function MockSectionControls({
  state, onCountChange, onGenerate, generateLabel, disabled,
}: MockSectionControlsProps) {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>
          {t("pro.system.mockData.count")}
        </label>
        <input
          type="number"
          min={1}
          max={50}
          value={state.count}
          onChange={(e) => onCountChange(e.target.value)}
          disabled={state.running}
          style={{
            width: 80, padding: "8px 10px", borderRadius: 6,
            border: "1px solid #D1D5DB", fontSize: 14,
            background: "var(--color-inputBg, #fff)",
            color: "var(--color-fg, #111)",
          }}
        />
      </div>
      <Button
        type="button"
        color="primary"
        loading={state.running}
        disabled={state.running || disabled}
        onClick={onGenerate}
      >
        {generateLabel}
      </Button>
      {state.done && !state.running && (
        <span style={{ fontSize: 13, color: "#16A34A", fontWeight: 500 }}>
          ✓ {t("pro.system.mockData.done")}
        </span>
      )}
    </div>
  );
}

interface MockSectionResultsProps {
  state: SectionState;
  resultLabel: (r: GeneratedRecord) => string | undefined;
  resultDetail: (r: GeneratedRecord) => string | undefined;
}

function MockSectionResults({ state, resultLabel, resultDetail }: MockSectionResultsProps) {
  const { t } = useI18n();
  if (state.error) {
    return <Callout variant="error">{state.error}</Callout>;
  }
  if (state.results.length === 0) return null;

  return (
    <div style={{
      border: "1px solid #E5E7EB", borderRadius: 8,
      overflow: "hidden", maxHeight: 240, overflowY: "auto",
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600, color: "#374151" }}>
              {t("pro.system.mockData.results.col.name")}
            </th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600, color: "#374151" }}>
              {t("pro.system.mockData.results.col.detail")}
            </th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600, color: "#6B7280", fontFamily: "monospace" }}>
              {t("pro.system.mockData.results.col.id")}
            </th>
          </tr>
        </thead>
        <tbody>
          {state.results.map((r, i) => (
            <tr key={`${r.id}-${i}`} style={{ borderBottom: i < state.results.length - 1 ? "1px solid #F3F4F6" : "none" }}>
              <td style={{ padding: "7px 12px", fontWeight: 500 }}>{resultLabel(r)}</td>
              <td style={{ padding: "7px 12px", color: "#6B7280" }}>{resultDetail(r) ?? "—"}</td>
              <td style={{ padding: "7px 12px", color: "#9CA3AF", fontFamily: "monospace", fontSize: 11 }}>{r.id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
